import { inject, Injectable } from '@angular/core';
import { ToastsService } from '@catch/services';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, exhaustMap, map, mergeMap, tap } from 'rxjs/operators';
import { FacilitiesAdapter } from '../../adapters/facilities.adapter';
import {
  createFacility,
  createFacilitySuccess,
  deleteFacility,
  deleteFacilityFailure,
  deleteFacilitySuccess,
  loadFacilities,
  loadFacilitiesFailure,
  loadFacilitiesSuccess,
  updateFacility,
  updateFacilityFailure,
  updateFacilitySuccess,
} from './facilities.actions';

@Injectable()
export class FacilityEffects {
  private readonly actions$ = inject(Actions);
  private readonly facilitiesAdapter = inject(FacilitiesAdapter);
  private readonly toastsService = inject(ToastsService);
  
  loadFacilities$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadFacilities),
      exhaustMap(() =>
        this.facilitiesAdapter.getFacilities().pipe(
          map((facilities) => loadFacilitiesSuccess({ facilities })),
          catchError((error) => {
            return of(loadFacilitiesFailure({ error }));
          })
        )
      )
    )
  );

  createFacility$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createFacility),
      mergeMap((props) =>
        this.facilitiesAdapter.createFacility(props.facility).pipe(
          map((response) => createFacilitySuccess({ facility: { ...props.facility, ...response, valid: true } })),
          catchError((error) => {
            return of(deleteFacilityFailure({ error }));
          })
        )
      )
    )
  );

  createFacilitySuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(createFacilitySuccess),
        tap(() => {
          this.toastsService.success('company-tool.views.settings.company.facilities.toasts.created');
        })
      ),
    { dispatch: false }
  );

  updateFacility$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateFacility),
      mergeMap((props) =>
        this.facilitiesAdapter.editFacility(props.facility).pipe(
          map(() => updateFacilitySuccess({ facility: props.facility })),
          catchError((error) => {
            return of(updateFacilityFailure({ error }));
          })
        )
      )
    )
  );

  updateFacilitySuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(updateFacilitySuccess),
        tap(() => {
          this.toastsService.success('company-tool.views.settings.company.facilities.toasts.updated');
        })
      ),
    { dispatch: false }
  );

  deleteFacility$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteFacility),
      mergeMap((props) =>
        this.facilitiesAdapter.deleteFacility(props.facility).pipe(
          map(() => deleteFacilitySuccess({ id: props.facility.id })),
          catchError((error) => {
            return of(deleteFacilityFailure({ error }));
          })
        )
      )
    )
  );

  deleteFacilitySuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(deleteFacilitySuccess),
        tap(() => {
          this.toastsService.success('company-tool.views.settings.company.facilities.toasts.deleted');
        })
      ),
    { dispatch: false }
  );

  loadFacilitiesFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadFacilitiesFailure),
        tap((action) => {
          // Todo: Add proper error handling. Maybe with a toasty?
          //alert(action.error.message);
        })
      ),
    { dispatch: false }
  );
}
